import React, { useState } from 'react';
import { Form, Input, Checkbox, Icon, Popup } from 'semantic-ui-react';
import { NumericFormat, NumberFormatValues } from 'react-number-format';

type NewEnergySystemValues = {
    newEnergySystem?: string | null;
    costOfNewEnergySystem?: number | null;
    energySystemSize?: number | null;
    energyProductionYear1?: number | null;
    expectedElectricBillSavingsYear1?: number | null;
    expectedBillEscalationRate?: number | null;
    renewableEnergyCreditsValue?: number | null;
    operationMaintenanceCosts?: number | null;
    includeInverterReplacement?: boolean | null;
};

type Props = {
    values?: NewEnergySystemValues;
    onChange: (values: NewEnergySystemValues) => void;
};

const NewEnergySystem: React.FC<Props> = ({ values = {}, onChange }) => {
    const [formValues, setFormValues] = useState<NewEnergySystemValues>({
        newEnergySystem: values.newEnergySystem ?? '',
        costOfNewEnergySystem: values.costOfNewEnergySystem ?? null,
        energySystemSize: values.energySystemSize ?? null,
        energyProductionYear1: values.energyProductionYear1 ?? null,
        expectedElectricBillSavingsYear1: values.expectedElectricBillSavingsYear1 ?? null,
        expectedBillEscalationRate: values.expectedBillEscalationRate ?? 3,
        renewableEnergyCreditsValue: values.renewableEnergyCreditsValue ?? null,
        operationMaintenanceCosts: values.operationMaintenanceCosts ?? null,
        includeInverterReplacement: values.includeInverterReplacement ?? true,
    });

    // useEffect(() => {
    //     onChange(formValues);
    // }, [formValues, onChange]);

    const handleChange = (
        name: string,
        value: any
    ) => {
        let updatedValues = {
            ...formValues,
            [name]: value === ''
                ? null
                : Number(value),
        };
        if (name === 'energySystemSize' && value) {
            updatedValues = {
                ...updatedValues,
                operationMaintenanceCosts: value === ''
                    ? null
                    : Number(value) * 10,
            };
        }
        setFormValues(updatedValues);
        onChange(updatedValues);
    };

    const handleCheckboxChange = (
        _e: React.FormEvent<HTMLInputElement>,
        data: { name?: string; checked?: boolean }
    ) => {
        const { name, checked } = data;
        if (name === undefined) return;
        const updatedValues = {
            ...formValues,
            includeInverterReplacement: checked,
        };
        setFormValues(updatedValues);
        onChange(updatedValues);
    };

    return (
        <Form>

            <Form.Field>
                <label className="required">Price of new energy system</label>
                <NumericFormat
                    thousandSeparator
                    customInput={Input}
                    name="costOfNewEnergySystem"
                    value={formValues.costOfNewEnergySystem ?? ''}
                    onValueChange={(values: NumberFormatValues) => handleChange('costOfNewEnergySystem', values.value)}
                    placeholder="Enter price..."
                    label={{ basic: true, content: '$' }}
                    labelPosition="right"
                    min={0}
                />
            </Form.Field>
            <Form.Field>
                <label className="required">Size of new energy system</label>
                <NumericFormat
                    thousandSeparator
                    customInput={Input}
                    name="energySystemSize"
                    value={formValues.energySystemSize ?? ''}
                    onValueChange={(values: NumberFormatValues) => handleChange('energySystemSize', values.value)}
                    placeholder="Enter size..."
                    label={{ basic: true, content: 'kWdc' }}
                    labelPosition="right"
                    min={0}
                />
            </Form.Field>
            <Form.Field>
                <label className="required">Energy production (year 1)</label>
                <NumericFormat
                    thousandSeparator
                    customInput={Input}
                    name="energyProductionYear1"
                    value={formValues.energyProductionYear1 ?? ''}
                    onValueChange={(values: NumberFormatValues) => handleChange('energyProductionYear1', values.value)}
                    placeholder="Enter production..."
                    label={{ basic: true, content: 'kWh' }}
                    labelPosition="right"
                    min={0}
                />
            </Form.Field>
            <Form.Field>
                <label className="required">Expected electric bill savings (year 1)</label>
                <NumericFormat
                    thousandSeparator
                    customInput={Input}
                    name="expectedElectricBillSavingsYear1"
                    value={formValues.expectedElectricBillSavingsYear1 ?? ''}
                    onValueChange={(values: NumberFormatValues) => handleChange('expectedElectricBillSavingsYear1', values.value)}
                    placeholder="Enter savings..."
                    label={{ basic: true, content: '$' }}
                    labelPosition="right"
                    min={0}
                />
            </Form.Field>
            <Form.Field>
                <label className="required">Expected electric bill escalation rate</label>
                <NumericFormat
                    thousandSeparator
                    customInput={Input}
                    name="expectedBillEscalationRate"
                    value={formValues.expectedBillEscalationRate ?? 3}
                    onValueChange={(values: NumberFormatValues) => handleChange('expectedBillEscalationRate', values.value)}
                    placeholder="Enter rate..."
                    label={{ basic: true, content: '%' }}
                    labelPosition="right"
                    step="0.01"
                    min={0}
                />
            </Form.Field>

            <Form.Field>
                <label>Operation and maintenance costs (1 year)</label>
                <NumericFormat
                    thousandSeparator
                    customInput={Input}
                    name="operationMaintenanceCosts"
                    value={formValues.operationMaintenanceCosts ?? ''}
                    onValueChange={(values: NumberFormatValues) => handleChange('operationMaintenanceCosts', values.value)}
                    placeholder="Enter cost..."
                    label={{ basic: true, content: '$' }}
                    labelPosition="right"
                    min={0}
                />
            </Form.Field>
            <Form.Field>
                <label>
                    Include solar inverter replacement cost?
                </label>
                <Checkbox
                    toggle
                    name="includeInverterReplacement"
                    checked={!!formValues.includeInverterReplacement}
                    onChange={(e, data) => handleCheckboxChange(e, data)}
                    label={formValues.includeInverterReplacement ? 'Yes' : 'No'}
                />
            </Form.Field>
            <Form.Field>
                <label>
                    Value of Renewable Energy Credits (1 year)
                </label>
                <NumericFormat
                    thousandSeparator
                    customInput={Input}
                    name="renewableEnergyCreditsValue"
                    value={formValues.renewableEnergyCreditsValue ?? ''}
                    onValueChange={(values: NumberFormatValues) => handleChange('renewableEnergyCreditsValue', values.value)}
                    placeholder="Enter value..."
                    label={{ basic: true, content: '$' }}
                    labelPosition="right"
                    min={0}
                />
            </Form.Field>


        </Form>
    );
};

export default NewEnergySystem;